import { ComponentType, useEffect } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';
import { CCPOfferingContent } from 'src/contentful/product-catalog';

import { PriceCardsStack } from '@/components/adg4/price-card/src/price-cards-stack';
import { useGetComparePlanCta } from '@/components/wpl/pricing/src/pricing-content-context.tsx';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';

import {
	CloudOfferingKey,
	CloudOfferingsWithSkus,
	CloudProductKey,
	DefaultPlanLimits,
	OfferingHamsMap,
	OfferingSKUMap,
	PricingPeriod,
	PricingPlans,
	ProductPlan,
	SpecialPlanNames,
	TRELLO_CTA_URL,
	Edition,
	CtaBtnText,
	PRICE_FINDER_CONTAINER,
	CurrencyISOCode,
} from '../constants';
import { usePriceFinderContext } from '../price-finder-context.tsx';
import { usePriceFinderStore } from '../store/store';
import { usePriceFinderRequests, usePricing } from '../utils/use-price-finder-data';

import { CtaButton } from './cta-button';
import { PriceCard } from './price-card';

export const centerJustifiedStyling = {
	display: 'flex',
	flexDirection: 'column',
	flexGrow: '1',
	justifyContent: 'center',
	lineHeight: '24px',
};

type PriceCardsContainerProps = {
	productKey: string;
	signupURL: string;
	contactURL: string;
	hamsProductKey: string;
	hamsPricingData: OfferingHamsMap<CloudProductKey, PricingPlans>;
	ccpPricingData: OfferingSKUMap<CloudOfferingKey, CCPOfferingContent>;
	localeISOCode: string;
};

export const PriceCardsContainer = ({
	productKey,
	signupURL,
	contactURL,
	hamsProductKey,
	hamsPricingData,
	ccpPricingData,
	localeISOCode,
}: PriceCardsContainerProps) => {
	const intl = useIntl();
	let forcedUsdPricing = undefined;
	const {
		dataProductKey,
		productPlans,
		enterpriseMinimumCount = DefaultPlanLimits.UNIT_MIN_ENTERPRISE,
	} = usePriceFinderContext();

	const [{ unitCount, billingCycle, currencyISOCode }, actions] = usePriceFinderStore();

	const recommendedPlanName = getRecommendedPlanName(unitCount, productPlans);

	const fetchPriceCalcs = usePriceFinderRequests(
		hamsProductKey,
		productPlans,
		enterpriseMinimumCount,
		unitCount,
		billingCycle,
	);

	if (dataProductKey === 'trello') {
		forcedUsdPricing = 'USD';
	}
	const finalPriceCalcs = usePricing(
		ccpPricingData,
		hamsPricingData,
		fetchPriceCalcs,
		(forcedUsdPricing || currencyISOCode) as CurrencyISOCode,
		localeISOCode,
	);

	useEffect(() => {
		actions.updatePriceCalcs(finalPriceCalcs);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [unitCount, billingCycle, currencyISOCode]);

	const localizedSignupURL = useLocalizedHref(signupURL);
	const localizedContactURL = useLocalizedHref(contactURL);

	const ctaButtonText = intl.formatMessage({
		id: 'Pricing.PriceCard.StartFreeTrial',
		defaultMessage: CtaBtnText.START_FREE_TRIAL,
	});

	const getProductPlanProps = (params: {
		idx: number;
		productPlan: ProductPlan;
	}): ProductPlanPriceCardProps => ({
		idx: params.idx,
		productPlan: params.productPlan,
		dataProductKey: dataProductKey,
		recommendedPlanName: recommendedPlanName,
		unitCount: unitCount,
		ctaButtonText: ctaButtonText,
		localizedSignupURL: localizedSignupURL,
		localizedContactURL: localizedContactURL,
		billingCycle: billingCycle,
		currencyISOCode: currencyISOCode,
		productKey: productKey,
	});

	const hasFeatures = productPlans[0]?.planFeatureMessages?.length;
	return (
		<div
			css={css({
				display: 'flex',
				justifyContent: 'center',
				width: '100%',
			})}
		>
			<PriceCardsStack sections={hasFeatures ? 2 : 1}>
				{productPlans.map((productPlan, idx) => (
					<ProductPlanPriceCard
						key={productPlan.planName}
						{...getProductPlanProps({ idx, productPlan })}
					/>
				))}
			</PriceCardsStack>
		</div>
	);
};

export interface PriceCardProductPlan extends ProductPlan {
	planTitleComponent?: ComponentType<{ planName: string }>;
}

export interface ProductPlanPriceCardProps {
	productPlan: PriceCardProductPlan;
	idx: number;
	dataProductKey: CloudProductKey;
	recommendedPlanName: string;
	unitCount: number;
	ctaButtonText: string;
	localizedSignupURL: string;
	localizedContactURL: string;
	billingCycle: PricingPeriod;
	currencyISOCode: string;
	productKey: string;
}

// eslint-disable-next-line complexity
const ProductPlanPriceCard = (props: ProductPlanPriceCardProps) => {
	const intl = useIntl();
	const contactSalesText = intl.formatMessage({
		id: 'Pricing.PriceCard.ContactSales',
		defaultMessage: CtaBtnText.CONTACT_SALES,
	});

	const {
		productPlan,
		idx,
		dataProductKey,
		recommendedPlanName,
		unitCount,
		ctaButtonText,
		localizedSignupURL,
		localizedContactURL,
		billingCycle,
		currencyISOCode,
		productKey,
	} = props;

	const { productPlans, shouldAddDiscount, ctaUrlSource } = usePriceFinderContext();
	const getComparePlanCta = useGetComparePlanCta();
	const { signupURLSuffix, planFeatureMessages, description, signupLimit, offeringSku } =
		productPlan;

	const { planTitleComponent } = productPlan;
	let { planName } = productPlan;

	let edition = offeringSku?.toLowerCase().match(/free|standard|premium|enterprise/)?.[0];

	const isHighlighted = planName === recommendedPlanName || productPlans.length === 1;
	let dataEventPropsLabel = `${planName}-start-trial`;
	let ctaBtnText = ctaButtonText;
	let ctaBtnUrl = '';
	let rovoStartTrialUrl = '';
	const localizedUrlPath = useLocalizedHref(productPlan.signupURLInfo?.urlPath ?? '');

	if (productPlan.signupURLInfo) {
		const { shouldLocalize, urlDomain, urlPath } = productPlan.signupURLInfo;
		if (shouldLocalize) {
			ctaBtnUrl = `${urlDomain}${localizedUrlPath}`;
			rovoStartTrialUrl = `${urlDomain}${localizedUrlPath}`;
		} else {
			ctaBtnUrl = `${urlDomain}${urlPath}`;
			rovoStartTrialUrl = `${urlDomain}${urlPath}`;
		}
	} else {
		ctaBtnUrl = localizedSignupURL + (signupURLSuffix || '');
	}

	const overSignupLimit = unitCount > (signupLimit || DefaultPlanLimits.UNIT_LIMIT);

	if (offeringSku === CloudOfferingsWithSkus.rovo || dataProductKey === 'rovo') {
		planName = 'Rovo';

		if (unitCount > 1000) {
			ctaBtnUrl = localizedContactURL;
			ctaBtnText = contactSalesText;
		}
	}

	const pricingPlanId = '';

	if (dataProductKey === 'trello') {
		ctaBtnUrl = TRELLO_CTA_URL;
	}

	switch (planName) {
		case SpecialPlanNames.STARTER:
		case SpecialPlanNames.FREE:
			edition = Edition.FREE;
			dataEventPropsLabel = `${planName}-get-it-now`;
			ctaBtnText = intl.formatMessage({
				id: 'Pricing.PriceCard.GetItNow',
				defaultMessage: CtaBtnText.GET_IT_NOW,
			});
			break;

		case SpecialPlanNames.ENTERPRISE:
			edition = Edition.ENTERPRISE;
			dataEventPropsLabel = `${planName}-contact-sales`;
			ctaBtnText = contactSalesText;
			ctaBtnUrl = productPlan.signupURLInfo ? ctaBtnUrl : localizedContactURL;
			break;
	}

	const shouldDisableCta =
		overSignupLimit &&
		planName !== SpecialPlanNames.FREE &&
		planName !== SpecialPlanNames.ENTERPRISE;

	const planTitleId = `planTitle_${idx}`;
	const planDescriptionId = `planDesc_${idx}`;
	const ctaId = `planCta_${idx}`;

	const ctaAriaLabelledBy = `${planTitleId} ${planDescriptionId} ${ctaId}`;

	const contentfulCta = getComparePlanCta({ comparePlanIdx: idx });
	const localizedContentfulCtaUrl = useLocalizedHref(contentfulCta?.ctaUrl ?? '');

	let ctaButtonUrl = ctaBtnUrl;
	if (contentfulCta?.ctaUrl && ctaUrlSource !== 'productPlans') {
		ctaButtonUrl = localizedContentfulCtaUrl;
	}

	const urlWithSignupSource = new URL(ctaButtonUrl, window.location.origin);
	urlWithSignupSource.searchParams.set('signupSource', 'pricing');
	ctaButtonUrl = urlWithSignupSource.toString();

	let ctaButton = (
		<CtaButton
			id={ctaId}
			edition={edition}
			dataProductKey={dataProductKey}
			type={isHighlighted ? 'primary' : 'secondary'}
			text={contentfulCta?.ctaButtonText ?? ctaBtnText}
			dataEventPropsLabel={dataEventPropsLabel}
			url={ctaButtonUrl}
			isDisabled={shouldDisableCta}
			containerId={PRICE_FINDER_CONTAINER}
			aria-labelledby={ctaAriaLabelledBy}
		/>
	);

	if (offeringSku === CloudOfferingsWithSkus.rovo && unitCount > 1000) {
		ctaButton = (
			<div css={css({ display: 'flex', gap: '5px' })} data-testid="rovo-double-cta">
				{ctaButton}
				<CtaButton
					id={ctaId}
					edition={edition}
					dataProductKey={dataProductKey}
					type={'secondary'}
					text={ctaButtonText}
					dataEventPropsLabel={dataEventPropsLabel}
					url={rovoStartTrialUrl}
					isDisabled={overSignupLimit}
					target="_self"
					aria-labelledby={ctaAriaLabelledBy}
					containerId={PRICE_FINDER_CONTAINER}
				/>
			</div>
		);
	}

	return (
		<PriceCard
			key={idx}
			planName={planName}
			planTitleComponent={planTitleComponent}
			description={description}
			highlighted={isHighlighted}
			ctaButton={ctaButton}
			planFeatureMessages={planFeatureMessages}
			adjacentLowerTierPlanName={productPlans[idx - 1]?.planName}
			first={idx === 0}
			last={idx === productPlans.length - 1}
			billingCycle={billingCycle}
			overSignupLimit={overSignupLimit}
			currencyISOCode={currencyISOCode}
			unitCount={unitCount}
			productKey={productKey}
			priceCalcIdx={idx}
			contactURL={localizedContactURL}
			offeringSku={offeringSku}
			enableRecommendation={productPlans.length > 1}
			shouldAddDiscount={shouldAddDiscount}
			pricingPlanId={pricingPlanId}
			edition={edition}
			dataProductKey={dataProductKey}
		/>
	);
};

const getRecommendedPlanIdx = (unitCount: number, minUnitCounts: number[]) => {
	const len = minUnitCounts.length - 1;

	return len - minUnitCounts.reverse().findIndex((minUnitCount) => unitCount > minUnitCount);
};

export const getRecommendedPlanName = (unitCount: number, productPlans: ProductPlan[]) => {
	const recommendedPlanIdx = getRecommendedPlanIdx(
		unitCount,
		productPlans.map((productPlan) => productPlan.recommendationLimit),
	);

	return productPlans[recommendedPlanIdx]?.planName ?? '';
};
